import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { BrandColours } from "@/pages/admin/BrandColours";

const B = process.env.REACT_APP_BACKEND_URL;

export const BrandingSettings = () => {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    api.get("/biz/branding").then((r) => setInfo(r.data));
  }, []);
  if (!info) return null;
  return (
    <>
      <LogoUpload brand={info.brand} onChange={(brand, palette) => setInfo({ ...info, brand, palette: palette ?? info.palette })} />
      <BrandColours palette={info.palette} saved={info.colors} onSaved={(colors) => setInfo({ ...info, colors })} />
    </>
  );
};

const LogoUpload = ({ brand, onChange }) => {
  const input = useRef(null);
  const [bg, setBg] = useState(brand?.background || "#ffffff");
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Logo must be smaller than 5 MB");
    const form = new FormData();
    form.append("file", file);
    form.append("background", bg);
    setBusy(true);
    try {
      const { data } = await api.post("/biz/branding/logo", form);
      const fresh = (await api.get("/biz/branding")).data;
      onChange(data.brand, fresh.palette);
      toast.success("Logo saved. Staff who already installed the app may need to re-add it to see the new icon");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  };
  const remove = async () => {
    try {
      await api.delete("/biz/branding/logo");
      onChange(null, []);
      toast.success("Logo removed");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-3 border-t border-slate-100 pt-4" data-testid="branding-settings">
      <p className="text-sm font-semibold">Your logo on the staff app</p>
      <p className="text-xs text-slate-500">Used for the app icon on staff phones, the join page and notifications. A square PNG at least 512 pixels works best.</p>
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-2xl border border-slate-200 bg-slate-50" data-testid="branding-icon-preview">
          {brand ? <img src={`${B}${brand.icon_192}`} alt="App icon" className="h-full w-full object-cover" /> : <span className="text-xs text-slate-400">No logo</span>}
        </div>
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-xs text-slate-600">Icon background
            <input type="color" value={bg} onChange={(e) => setBg(e.target.value)} className="h-7 w-10 cursor-pointer rounded border border-slate-200" data-testid="branding-bg-input" />
          </label>
          <div className="flex gap-2">
            <input ref={input} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="branding-file-input" />
            <Button size="sm" className="rounded-full" disabled={busy} onClick={() => input.current.click()} data-testid="branding-upload-btn"><ImagePlus className="mr-1 h-4 w-4" />{busy ? "Uploading…" : brand ? "Replace logo" : "Upload logo"}</Button>
            {brand && <Button size="sm" variant="ghost" onClick={remove} data-testid="branding-remove-btn"><Trash2 className="mr-1 h-4 w-4" />Remove</Button>}
          </div>
        </div>
      </div>
    </div>
  );
};
